import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from '../../redux/store';
import { actions } from '../../redux/modules/settingsApprovals';
import { I18n } from '@kineticdata/react';
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from 'reactstrap';
import { PageTitle } from '../shared/PageTitle';
import {
  NewApprovalGroupModal,
  NewApprovalManagerModal,
  NewApproverModal,
  NewApprovalFormModal,
} from './NewModals';
import {
  LoadingMessage,
  EmptyMessage,
  ErrorMessage,
  Utils,
  C,
  addToast,
  addToastAlert,
  openConfirm,
  selectServicesKapp,
} from '@kineticdata/bundle-common';
import { List } from 'immutable';
import classNames from 'classnames';

const AgreementTeamComponent = ({
  agreement,
  isValidAgreement,
  agreementsMap,
  init,
  error,
  team,
  master,
  children,
  agreementUsers,
  approvalFormsMap,
  servicesFormsWithApprovals,
  isCurrentApprovalManager,
  isCurrentMasterApprovalManager,
  isCustomerApprovalAdmin,
  actionsPending,
  // Actions
  fetchTeam,
  createApprovalTeam,
  deleteApprovalTeam,
  addApprovalManager,
  removeApprovalManager,
  addApprover,
  removeApprover,
  addApprovalForm,
  removeApprovalForm,
  // Other state
  profile,
  appLocation,
  isMobile,
  navigate,
}) => {
  useEffect(
    () => {
      if (isValidAgreement) {
        fetchTeam(agreement);
      }
    },
    [agreement, isValidAgreement, fetchTeam],
  );

  // State for managing modal for creating new entities
  const [newModalOpen, setNewModalOpen] = useState(false);
  // State for tracking current open approval group
  const [currentGroup, setCurrentGroup] = useState(null);
  const currentGroupTeam = `${agreement}::${currentGroup}`;
  const selectGroup = useCallback(group => () => setCurrentGroup(group), []);
  const currentTeam = currentGroup
    ? children.find(t => t.name === currentGroupTeam)
    : null;

  const approvalManagerUsernames = useMemo(
    () => (team ? team.memberships.map(m => m.user.username) : []),
    [team],
  );

  // Button callbacks
  const handleDeleteApprovalGroup = useCallback(
    () =>
      openConfirm({
        title: 'Delete Approval Group',
        body: `Are you sure you want to delete the '${currentGroup}' approval group?`,
        actionName: 'Delete',
        ok: () => {
          deleteApprovalTeam({
            teamName: currentGroupTeam,
            success: submission => {
              setCurrentGroup();
              addToast('Approval group deleted successfully');
            },
            failure: error => {
              addToastAlert({
                severity: 'danger',
                title: 'Failed to delete approval group',
                message: error.message,
              });
            },
          });
        },
      }),
    [deleteApprovalTeam, currentGroup, currentGroupTeam],
  );
  const handleRemoveApprovalManager = useCallback(
    user => () =>
      openConfirm({
        title: 'Remove Approval Manager',
        body: `Are you sure you want to remove '${user.displayName ||
          user.username}' from the list of approval managers?`,
        actionName: 'Remove',
        ok: () => {
          removeApprovalManager({
            teamName: agreement,
            username: user.username,
            success: submission => {
              addToast('Approval manager removed successfully');
            },
            failure: error => {
              addToastAlert({
                severity: 'danger',
                title: 'Failed to remove approval manager',
                message: error.message,
              });
            },
          });
        },
      }),
    [removeApprovalManager, agreement],
  );
  const handleRemoveApprover = useCallback(
    user => () =>
      openConfirm({
        title: 'Remove Approver',
        body: `Are you sure you want to remove '${user.displayName ||
          user.username}' from the list of approvers?`,
        actionName: 'Remove',
        ok: () => {
          removeApprover({
            teamName: currentGroupTeam,
            username: user.username,
            success: submission => {
              addToast('Approver removed successfully');
            },
            failure: error => {
              addToastAlert({
                severity: 'danger',
                title: 'Failed to remove approver',
                message: error.message,
              });
            },
          });
        },
      }),
    [removeApprover, currentGroupTeam],
  );
  const handleRemoveApprovalForm = useCallback(
    approvalForm => () =>
      openConfirm({
        title: 'Remove Service Offering',
        body: `Are you sure you want to remove '${approvalForm['Form Name'] ||
          approvalForm[
            'Form Slug'
          ]}' from the list of assigned service offerings?`,
        actionName: 'Remove',
        ok: () => {
          removeApprovalForm({
            id: approvalForm.id,
            success: submission => {
              addToast('Service offerings removed successfully');
            },
            failure: error => {
              addToastAlert({
                severity: 'danger',
                title: 'Failed to remove service offering',
                message: error.message,
              });
            },
          });
        },
      }),
    [removeApprovalForm],
  );

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          parts={[agreement, 'Approval Management']}
          breadcrumbs={[{ label: `Approval Management`, to: '..' }]}
          title={agreement}
          actions={[
            {
              label: 'Create Approval Group',
              onClick: () => setNewModalOpen('approvalgroup'),
              menu: true,
            },
            !!currentGroup && {
              label: 'Delete Approval Group',
              onClick: handleDeleteApprovalGroup,
              menu: true,
            },
          ]}
        />

        {newModalOpen === 'approvalgroup' && (
          <NewApprovalGroupModal
            agreement={agreement}
            toggle={() => setNewModalOpen(false)}
            createApprovalTeam={createApprovalTeam}
            onSuccess={team => setCurrentGroup(team.name.split('::')[1])}
          />
        )}

        {newModalOpen === 'approvalmanager' && (
          <NewApprovalManagerModal
            agreement={agreement}
            toggle={() => setNewModalOpen(false)}
            addApprovalManager={addApprovalManager}
            isCustomerApprovalAdmin={isCustomerApprovalAdmin}
            options={agreementUsers}
            existingUsernames={approvalManagerUsernames}
          />
        )}

        {newModalOpen === 'approver' && (
          <NewApproverModal
            agreement={agreement}
            group={currentGroup}
            toggle={() => setNewModalOpen(false)}
            addApprover={addApprover}
            options={agreementUsers}
            existingUsernames={
              team ? team.memberships.map(m => m.user.username) : []
            }
          />
        )}
        {newModalOpen === 'approvalform' && (
          <NewApprovalFormModal
            agreement={agreement}
            group={currentGroup}
            toggle={() => setNewModalOpen(false)}
            addApprovalForm={addApprovalForm}
            options={servicesFormsWithApprovals}
            existingForms={(
              (approvalFormsMap && approvalFormsMap.get(currentGroupTeam)) ||
              List()
            ).map(f => f['Form Slug'])}
          />
        )}

        {!isValidAgreement ? (
          <ErrorMessage message={`${agreement} is not a valid agreement.`} />
        ) : error ? (
          <ErrorMessage message={error.message} />
        ) : !init ? (
          <LoadingMessage />
        ) : !isCustomerApprovalAdmin && !isCurrentApprovalManager ? (
          <ErrorMessage message="You do not have permission to manage the approval groups for this agreement." />
        ) : !team ? (
          <ErrorMessage
            message={`An approval team for the agreement ${agreement} doesn't exist and you do not have permission to create it.`}
          />
        ) : (
          <div className="column-container ">
            <div className="column-panel column-panel--small">
              <div className="side-nav">
                {isMobile ? (
                  <UncontrolledDropdown className="side-nav__dropdown">
                    <DropdownToggle
                      color="dropdown"
                      className={classNames('side-nav__toggle')}
                    >
                      <span className="label">
                        <I18n>
                          {currentGroup || <I18n>Approval Managers</I18n>}
                        </I18n>
                      </span>
                      <span
                        className={`ml-auto fa fa-fw fa-angle-down text-muted`}
                      />
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={selectGroup()}>
                        <span className="label">
                          <I18n>Approval Managers</I18n>
                        </span>
                        {team && (
                          <span className="d-flex align-items-center ml-auto pl-2 text-muted">
                            {team.memberships.length}
                            <span className="fa fa-user-tie fa-sm fa-fw m-0" />
                          </span>
                        )}
                      </DropdownItem>
                      <div className="side-nav__divider" />
                      <div className="side-nav__header">
                        <I18n>Approval Groups</I18n>
                      </div>
                      <div className="side-nav__group">
                        {children.map(t => {
                          const groupName = t.name.split('::')[1];
                          const formCount = approvalFormsMap
                            ? (approvalFormsMap.get(t.name) || List()).size
                            : null;
                          return (
                            <DropdownItem
                              key={t.name}
                              onClick={selectGroup(groupName)}
                            >
                              <span className="label">
                                <I18n>{groupName}</I18n>
                              </span>
                              <span className="d-flex align-items-center ml-auto pl-2 text-muted">
                                {t.memberships.length}
                                <span className="fa fa-user fa-sm fa-fw m-0" />
                              </span>
                              {!!approvalFormsMap && (
                                <span className="d-flex align-items-center justify-content-end pl-2 text-muted">
                                  {formCount}
                                  <span className="fa fa-file fa-sm fa-fw m-0" />
                                </span>
                              )}
                            </DropdownItem>
                          );
                        })}
                        {children.size === 0 && (
                          <div className="side-nav__info">
                            <I18n>None</I18n>
                          </div>
                        )}
                      </div>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                ) : (
                  <div className="side-nav__group">
                    <button
                      className={classNames({ active: !currentGroup })}
                      onClick={selectGroup()}
                    >
                      <span className="label">
                        <I18n>Approval Managers</I18n>
                      </span>
                      {team && (
                        <span className="d-flex align-items-center ml-auto pl-2 text-muted">
                          {team.memberships.length}
                          <span className="fa fa-user-tie fa-sm fa-fw m-0" />
                        </span>
                      )}
                    </button>
                    <div className="side-nav__divider" />
                    <div className="side-nav__header">
                      <I18n>Approval Groups</I18n>
                    </div>
                    {children.map(t => {
                      const groupName = t.name.split('::')[1];
                      const formCount = approvalFormsMap
                        ? (approvalFormsMap.get(t.name) || List()).size
                        : null;
                      return (
                        <button
                          key={t.name}
                          className={classNames({
                            active: currentGroup === groupName,
                          })}
                          onClick={selectGroup(groupName)}
                        >
                          <span className="label">
                            <I18n>{groupName}</I18n>
                          </span>
                          <span className="d-flex align-items-center ml-auto pl-2 text-muted">
                            {t.memberships.length}
                            <span className="fa fa-user fa-sm fa-fw m-0" />
                          </span>
                          {!!approvalFormsMap && (
                            <span className="d-flex align-items-center justify-content-end pl-2 text-muted">
                              {formCount}
                              <span className="fa fa-file fa-sm fa-fw m-0" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {children.size === 0 && (
                      <div className="side-nav__info">
                        <I18n>None</I18n>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="column-panel column-panel--large">
              {!currentGroup && (
                /*** Approval managers list ***********************************/
                <div className="p-4 bg-light">
                  <div className="section__title">
                    <span className="title">
                      <I18n>Approval Managers</I18n>
                    </span>
                    {(isCustomerApprovalAdmin ||
                      isCurrentMasterApprovalManager) && (
                      <span className="controls">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setNewModalOpen('approvalmanager')}
                        >
                          <span className="fa fa-plus fa-fw" />
                          <I18n>Add Approval Manager</I18n>
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="scroll-wrapper-h border-white">
                    <table className="table table--settings">
                      <thead>
                        <tr>
                          <th>Display Name</th>
                          <th>Email</th>
                          <th>Username</th>
                          <th width="1%" />
                          {(isCustomerApprovalAdmin ||
                            isCurrentMasterApprovalManager) && (
                            <th width="1%" />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {team.memberships
                          .sort((a, b) => {
                            const aName = a.user.displayName
                              .split(' ')
                              .reverse()
                              .join(' ');
                            const bName = b.user.displayName
                              .split(' ')
                              .reverse()
                              .join(' ');
                            return aName > bName ? 1 : aName < bName ? -1 : 0;
                          })
                          .map(m => {
                            const isMaster =
                              master &&
                              !!master.memberships.find(
                                b => b.user.username === m.user.username,
                              );
                            return (
                              <tr key={m.user.username}>
                                <td>{m.user.displayName}</td>
                                <td>{m.user.email}</td>
                                <td>{m.user.username}</td>
                                <td>
                                  {isMaster ? (
                                    <span className="badge badge-pill badge-success font-weight-normal">
                                      <I18n>Master</I18n>
                                    </span>
                                  ) : null}
                                </td>
                                {(isCustomerApprovalAdmin ||
                                  isCurrentMasterApprovalManager) && (
                                  <td>
                                    <button
                                      className="btn btn-text-danger"
                                      disabled={
                                        isMaster && !isCustomerApprovalAdmin
                                      }
                                      onClick={handleRemoveApprovalManager(
                                        m.user,
                                      )}
                                    >
                                      <span className="fa fa-times-circle fa-fw" />
                                      <I18n>Remove</I18n>
                                    </button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                      </tbody>
                      {team.memberships.length === 0 && (
                        <tfoot>
                          <tr>
                            <td colSpan="5">
                              <EmptyMessage title="There are no approval managers." />
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {!!currentTeam && (
                /*** Currently selected approval group ************************/
                <>
                  <div className="section__title">
                    <span className="title">
                      <I18n>{currentGroup}</I18n>
                    </span>
                  </div>
                  <div className="p-4 bg-light">
                    <div className="section__title">
                      <span className="title">
                        <I18n>Approvers</I18n>
                      </span>
                      <span className="controls">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setNewModalOpen('approver')}
                        >
                          <span className="fa fa-plus fa-fw" />
                          <I18n>Add Approver</I18n>
                        </button>
                      </span>
                    </div>
                    <div className="scroll-wrapper-h border-white">
                      <table className="table table--settings">
                        <thead>
                          <tr>
                            <th>Display Name</th>
                            <th>Email</th>
                            <th>Username</th>
                            <th width="1%" />
                          </tr>
                        </thead>
                        <tbody>
                          {currentTeam.memberships
                            .concat(
                              team.memberships.map(m => ({
                                ...m,
                                manager: true,
                              })),
                            )
                            .sort((a, b) => {
                              const aName = a.user.displayName
                                .split(' ')
                                .reverse()
                                .join(' ');
                              const bName = b.user.displayName
                                .split(' ')
                                .reverse()
                                .join(' ');
                              return aName > bName ? 1 : aName < bName ? -1 : 0;
                            })
                            .map(m => {
                              return (
                                <tr key={m.user.username}>
                                  <td>{m.user.displayName}</td>
                                  <td>{m.user.email}</td>
                                  <td>{m.user.username}</td>
                                  <td className="text-center">
                                    {m.manager ? (
                                      <span className="badge badge-pill badge-success">
                                        <I18n>Manager</I18n>
                                      </span>
                                    ) : (
                                      <button
                                        className="btn btn-text-danger"
                                        disabled={false}
                                        onClick={handleRemoveApprover(m.user)}
                                      >
                                        <span className="fa fa-times-circle fa-fw" />
                                        <I18n>Remove</I18n>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                        {currentTeam.memberships.length === 0 &&
                          team.memberships.length === 0 && (
                            <tfoot>
                              <tr>
                                <td colSpan="4">
                                  <EmptyMessage title="There are no approvers." />
                                </td>
                              </tr>
                            </tfoot>
                          )}
                      </table>
                    </div>

                    <div className="section__title mt-5">
                      <span className="title">
                        <I18n>Service Offerings</I18n>
                      </span>
                      {!!approvalFormsMap && (
                        <span className="controls">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setNewModalOpen('approvalform')}
                          >
                            <span className="fa fa-plus fa-fw" />
                            <I18n>Add Service Offering</I18n>
                          </button>
                        </span>
                      )}
                    </div>
                    <div className="scroll-wrapper-h border-white">
                      <table className="table table--settings">
                        <thead>
                          <tr>
                            <th>Service Offering</th>
                            <th>Approval Type</th>
                            <th width="1%" />
                          </tr>
                        </thead>
                        <tbody>
                          {!!approvalFormsMap &&
                            (
                              approvalFormsMap.get(currentGroupTeam) || List()
                            ).map(f => {
                              return (
                                <tr key={f['Form Slug']}>
                                  <td>
                                    {f['Form Name']}
                                    {!!f['Funding Dependent'] ? ' [$]' : ''}
                                  </td>
                                  <td>{f['Type']}</td>
                                  <td className="text-center">
                                    <button
                                      className="btn btn-text-danger"
                                      disabled={false}
                                      onClick={handleRemoveApprovalForm(f)}
                                    >
                                      <span className="fa fa-times-circle fa-fw" />
                                      <I18n>Remove</I18n>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                        <tfoot>
                          {!approvalFormsMap ? (
                            <tr>
                              <td colSpan="3">
                                <ErrorMessage title="Failed to retrieve the assigned service offerings." />
                              </td>
                            </tr>
                          ) : (approvalFormsMap.get(currentGroupTeam) || List())
                            .size === 0 ? (
                            <tr>
                              <td colSpan="3">
                                <EmptyMessage title="There are no service offerings assigned to this approval group." />
                              </td>
                            </tr>
                          ) : (
                            <tr>
                              <td colSpan="3">
                                <small>
                                  [$]&nbsp;
                                  <em>
                                    <I18n>
                                      Denotes service offerings that require
                                      funding to process.
                                    </I18n>
                                  </em>
                                </small>
                              </td>
                            </tr>
                          )}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  // Current agreement team
  const team = state.settingsApprovals.agreementTeam;
  // Is the current user an approval manger of the current agreement team
  const isCurrentApprovalManager =
    state.settingsApprovals.agreementTeam &&
    !!state.settingsApprovals.agreementTeam.memberships.find(
      m => m.user.username === state.app.profile.username,
    );
  // Current agreement team's master team
  const master = state.settingsApprovals.agreementTeamMaster;
  // Is the current user a master approval manger of the current agreement team
  const isCurrentMasterApprovalManager =
    state.settingsApprovals.agreementTeamMaster &&
    !!state.settingsApprovals.agreementTeamMaster.memberships.find(
      m => m.user.username === state.app.profile.username,
    );
  // Services kapp to get its forms
  const servicesKapp = selectServicesKapp(state);
  return {
    isCustomerApprovalAdmin: state.settingsApp.isCustomerApprovalAdmin,
    agreementsMap: state.settingsApp.agreementsMap,
    isValidAgreement: state.settingsApp.agreementsMap.has(props.agreement),
    init:
      state.settingsApprovals.currentAgreementTeam === props.agreement &&
      (!!state.settingsApprovals.agreementTeam ||
        !state.settingsApprovals.agreementTeamLoading),
    error: state.settingsApprovals.agreementTeamError,
    team,
    isCurrentApprovalManager,
    master,
    isCurrentMasterApprovalManager,
    children: state.settingsApprovals.agreementTeamChildren,
    agreementUsers: state.settingsApprovals.agreementUsers,
    approvalFormsMap:
      state.settingsApprovals.approvalForms &&
      state.settingsApprovals.approvalForms.groupBy(f => f['Approval Group']),
    servicesFormsWithApprovals: servicesKapp
      ? List(
          servicesKapp.forms.filter(
            f =>
              f.type === C.REQUEST_FORM_TYPE &&
              Utils.hasAttributeValue(f, C.FORM_ATTR_APPROVALS, 'True'),
          ),
        )
      : List(),
    actionsPending: state.settingsApprovals.actionsPending > 0,
    // Other state
    profile: state.app.profile,
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchTeam: actions.fetchAgreementTeamRequest,
  fetchApprovalForms: actions.fetchApprovalFormsRequest,
  createApprovalTeam: actions.createApprovalTeamRequest,
  deleteApprovalTeam: actions.deleteApprovalTeamRequest,
  addApprovalManager: actions.addApprovalManagerRequest,
  removeApprovalManager: actions.removeApprovalManagerRequest,
  addApprover: actions.addApproverRequest,
  removeApprover: actions.removeApproverRequest,
  addApprovalForm: actions.addApprovalFormRequest,
  removeApprovalForm: actions.removeApprovalFormRequest,
};

export const AgreementTeam = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AgreementTeamComponent);
